<template>
    <div>
        <el-row>
            <el-col :span="4"><div class="grid-content"></div></el-col>
            <el-col :span="16">

                <div class="title">
                    <h1>Context Connection: A Product Creative Design
                        Method for Integrating Design Materials and
                        Design Resources
                    </h1>
                </div>

            </el-col>
            <el-col :span="4"><div class="grid-content"></div></el-col>
        </el-row>
        <el-row :gutter="40" style="margin-top: 50px">
            <el-col :span="6"><div class="grid-content"></div></el-col>
            <el-col :span="12">
                <div class="picture">
                    <img src="../assets/project14_1.jpg.webp">
                </div>
            </el-col>
            <el-col :span="6"><div class="grid-content"></div></el-col>
        </el-row>
        <el-row :gutter="40" style="margin-top: 50px">
            <el-col :span="2"><div class="grid-content"></div></el-col>

            <el-col :span="20">
                <div class="abstract">
                    This study investigates the design method of integrating design resources and design materials
                    with an explanation of the foundations of cultural psychology. It focuses on the integration of
                    design resources and design materials in product creative design. This article outlines the design
                    process of integrating design resources and design materials based on user centered design (UCD)
                    and develops a design tool named “Context Connector (CC)” using cultural symbols (as design
                    resources) and products empowered by machine learning (as design materials) as examples.
                    Because cultural symbols can affect how people use and perceive things, researchers have been
                    talking a lot lately about how to improve the user experience of machine learning-empowered
                    products. Through the experiment of finding designers to use the design method to complete
                    product creative design, it has been confirmed with the empirical evidence that this method can
                    assist designers, to a certain extent, in fusing cultural symbols and machine learning empowered
                    products more reasonably and innovatively in the design process, improving design efficiency and
                    experience. Finally, this paper analyses the benefits and drawbacks of CC as well as the scope for
                    future research and application of this design method.
                    <div class="paper_published">
                      <br><br>
                        Zou, N., Gong, Q.*, Li, X., Zhao, Z., Chai, C. (2023). Context Connection: A Product Creative Design Method for Integrating Design Materials and Design Resources. Creativity Research Journal, 36(4). （SSCI，Q1，学生一作）                    </div>
                </div>
            </el-col>

            <el-col :span="2"><div class="grid-content"></div></el-col>
        </el-row>
        <el-row :gutter="40" style="margin-top: 50px">
            <el-col :span="2"><div class="grid-content"></div></el-col>
            <el-col :span="8">
                <div class="picture">
                    <img src="../assets/project14_2.jpg.webp">
                </div>
            </el-col>
            <el-col :span="12">
                <div class="sub-title">
                    Material
                </div>
                <div class="text">
                    To conduct an empirical study of the method that proposed, this study developed a design tool called Context Connector (CC). The method of " context connection" served as the foundation of CC. This product creative design tool comes with a canvas called “matrix canvas” and a thorough online instruction manual that describes the suggested design process (Figure 2).
                </div>
            </el-col>

            <el-col :span="2"><div class="grid-content"></div></el-col>
        </el-row>
        <el-row :gutter="40" style="margin-top: 50px">
            <el-col :span="2"><div class="grid-content"></div></el-col>
            <el-col :span="12">
                <div class="sub-title">
                    Study selection and analysis
                </div>
                <div class="text">
                    The user's rectangle, the left-side and right-side circles, the contents of the circle, and the arrows on either side of the user are all identical to those in Figure 1. The abstracted design process is represented in the diagram by the circles with numbers. Step 1 deals with the study of the users’ ultimate needs for, Step 2 is about the recognition of users of ML-empowered products, Step 3 shows the contextual characteristics of ML-empowered products (i.e., product requirements), Step 4 is about the recognition of cultural symbols, Step 5 means characteristics of the contextual of cultural symbols (i.e., traits of cultural symbols), and Step 6 deals with the comparison of cultural symbol characteristics and product requirements to find the similarities.
                  <br><br>  The steps in the CC's manual (i.e., the process in the design practices provided in this section) and design process description are shown on the right part of Figure 3, with connecting lines demonstrating how these steps correspond to the abstract CC-based design process. The phases detailed in the manual provided to designers in this study are not fundamentally different from the abstract CC-based design process, although they do not correspond perfectly at each step, and the phrasing used to describe particular concepts differs. This is because the study hopes to provide designers with an operational process or concept description that is more in line with design practitioners' perceptions, so that designers do not misunderstand some of the terms proposed in this study and spend too much time understanding CC, which will affect their final design ideas.
                </div>
            </el-col>
            <el-col :span="8">
                <div class="picture">
                    <img src="../assets/project14_3.png.webp">
                </div>
            </el-col>
            <el-col :span="2"><div class="grid-content"></div></el-col>
        </el-row>

        <el-row :gutter="40" style="margin-top: 50px">
            <el-col :span="2"><div class="grid-content"></div></el-col>
            <el-col :span="8">
                <div class="picture">
                    <img src="../assets/project14_4.png.webp">

                </div>
            </el-col>
            <el-col :span="12">
                <div class="sub-title">
                    Matrix Canvas
                </div>
                <div class="text">
                    In accordance with the contextual characteristics, the designer must sequentially fill in the respective positions of the matrix canvas with the product requirements and traits of the cultural symbols, as well as the user data in the "Target user persona."
                  <br><br>  For instance, based on a questionnaire poll, the designer of the smart money saving helper project specifically targeted children between the ages of 6 and 12. To determine their demands for the product, the designer also performed questionnaire surveys and parent interviews. 15 needs were ultimately chosen. The designer then used literature reviews to compile a list of 12 qualities for “Puman”, a traditional Chinese piggybank. The designer then marked the two elements she believed to be connected on the canvas' intersecting grid and filled in the "Product definition" area with the final description of the product concept. For example, the designer carefully considered Puman's feature of "Shake it and the money in it will make sound" with the product requirement for "Reward will be given when the product reaches the savings target." She then came up with the corresponding product definition of "using hard cards to replace real money”, and “the built-in steering gear connection can jack up the gear and ejector combination of the upper part." The number of cells designated in each row as the sorting reference for the final product description should then be included in the "Ranking" column. The completed matrix canvas of this example is displayed in Figure 4. (See the case study section for the final design output of this case)

                </div>
            </el-col>

            <el-col :span="2"><div class="grid-content"></div></el-col>
        </el-row>
        <el-row :gutter="40" style="margin-top: 50px">
            <el-col :span="2"><div class="grid-content"></div></el-col>

            <el-col :span="12">
                <div class="sub-title">
                    Procedure
                </div>
                <div class="text">
                    This study recruited 25 designers with more than two years of product design learning experience and hold a 14-days-workshop. The 25 designers were all students majoring in industrial design or product design who volunteered to take part in our experiment. We purposely opted to recruit participants from undergraduate students at least in their third year because the majority of them maintained a high frequency of design creative activity in their daily life by performing design projects in design courses. They were also more enthusiastic about product concept creative design and had probably not created their own fixed creativity pattern for the time being, making them more suited for testers of a new product creative tool. Participants ranged in age from 22 to 26. Four of them were in their third year, one was working on her Ph.D. (she heard about our experiment and had a great desire to participate), and the others were in their fourth year. 4 male designers and 21 female designers participated in our experiment, which is fairly close to the balance of male and female in the university's design major where we recruited participants from.
                    <br><br> This study numbered participants using random sorting software and separated them into Groups A (No.14-No.25) and B (No.1-No.13). The researchers instructed each group to begin a conceptual design project using the identical design proposal (design an entity product, which has the characteristics of a certain traditional Chinese cultural symbol and uses ML technology as one of the product functions). 12 designers made up Group A. They were instructed to use CC for their projects and were asked to quickly prototype by 3D printing for a short user walk-through. With a total of 13 members, Group B were asked to conduct product creative design process based on brainstorming without user study. Before the experiment began, the researcher introduced CC to Group A designers. A midterm display was scheduled for both groups A and B on the eighth day of the workshop to check the progress and answer the questions, and a presenting report was scheduled for the workshop's final day. All designers ultimately finished their work. Figure 5 shows the procedure of the experimental. Appendix B is a list of the concepts and specifics for 25 design outcomes (Table B1).
                    <br><br>  Following the completion of the product creative designs by 25 designers, a questionnaire was developed to ask Group A to rate their experience on a seven-point Likert scale by answering three questions about "ease of use," two questions about "process of use," two questions about "creative outcomes," and five questions about "knowledge comprehension" (Atasoy and Martens 2016; Daalhuizen and Cash 2021; Jacobs et al. 2017). On a seven-point Likert scale, all 25 designers were asked to rate how well they understood UCD and how creative they thought they were. The Appendix B contains a list of the questionnaire's contents (Table B2). Twelve designers from group A were also interviewed about the same four topics as the questionnaire, and their feedback and suggestions were solicited.
                    <br><br>  Twenty industrial design master's or doctoral students were invited to assess the 25 concepts that were presented; all of them were not workshop participants. The 25 concepts were rated on three separate dimensions—"innovation of product function," "innovation of cultural symbol integration," and "overall evaluation of the concept"—using a seven-point Likert scale. This study also conducts a thorough investigation of three presented typical cases.

                </div>
            </el-col>
            <el-col :span="8">
                <div class="picture">
                    <img src="../assets/project14_6.jpg.webp">

                </div>
            </el-col>
            <el-col :span="2"><div class="grid-content"></div></el-col>
        </el-row>
        <el-row :gutter="40" style="margin-top: 50px">
            <el-col :span="2"><div class="grid-content"></div></el-col>

            <el-col :span="20">
                <div class="sub-title">
                    For more information, please go for the published paper.
                </div>
                <div class="text">

                </div>
            </el-col>

            <el-col :span="2"><div class="grid-content"></div></el-col>
        </el-row>
    </div>
</template>

<script>
    export default {
        name: "Project7"
    }
</script>

<style scoped>
    .paper_published {
  margin-top: 24px;
  font-size: 15px;
  color: #333;
  font-weight: 400;
  text-align: justify;
  line-height: 1.9;
  border-bottom: 1px solid #333;
  padding-bottom: 6px;
}
    .picture img{
        width: 100%;
    }
    .sub-title{
        margin-bottom: 20px;
        font-size: 15px;
        color: #666;
        font-weight: 400;
        text-align: justify;
    }
    .text{
        font-size: 15px;
        color: #333;
        font-weight: 100;
        text-align: justify;
    }
    .abstract{
        margin-top: 40px;
        font-size: 15px;
        color: #333;
        font-weight: 100;
        text-align: justify;
    }
    video{
        width: 100%;
    }
    .grid-content{
        min-height: 36px;
    }
    .video{
        margin-top: 100px;
    }
    .title{
        margin-top: 100px;
        text-align: center;

    }
    .title a{
        color: #333;
    }
    .title p{
        color: #aaa;
        line-height: 30px;
    }
    .title h1{
        font-size:30px;
        font-weight: 100;
        letter-spacing:6px;
        color: #333333;
    }
</style>